import { Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from 'next-themes';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { CookieConsent } from '@/components/CookieConsent';
import { LanguageSuggestionBanner } from '@/components/LanguageSuggestionBanner';
import DeviceLimitGate from '@/components/DeviceLimitGate';

import i18n, { restoreSavedLanguage } from '@/config/i18n';
import { getI18nForLang, isLangSkippablePath, urlLangOf } from '@/components/LangWrapper';
import { useTranslation } from 'react-i18next';
import HreflangTags from '@/components/HreflangTags';
import RouteTracker from '@/components/RouteTracker';
import HostBranding, { isBrandedHost } from '@/components/HostBranding';
import BrandedHostGuard from '@/components/BrandedHostGuard';
import { AuthProvider } from '@/hooks/useAuth';
import { UserPreferencesProvider } from '@/hooks/useUserPreferences';
import { SchemaScript, generateOrganizationSchema, generateWebSiteSchema } from '@/lib/seoHelpers';

const queryClient = new QueryClient();

/**
 * Suspense fallback for lazy route chunks. Replaces the old empty
 * `min-h-screen` div, which on the dark (default) theme painted a pure
 * black viewport for the whole chunk download — the designer chunk can
 * take 10s+ on slow connections, so hard loads of non-prerendered routes
 * (/demo, /try, /dashboard, /cctv-design) looked dead.
 *
 * Deliberately near-textless: route-specific namespaces (e.g.
 * cctv-designer) may not be loaded yet, so the only string is an
 * sr-only label from the always-bundled default namespace. Reuses the
 * loader-breathe/shimmer keyframes from ProjectLoadingOverlay
 * (index.css), which also respect prefers-reduced-motion.
 */
const RouteLoadingFallback = () => {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-busy="true"
      className="flex min-h-screen flex-col items-center justify-center bg-white dark:bg-graphite-900"
    >
      <img
        src="/logos/logo-mark.svg"
        alt=""
        draggable={false}
        className="animate-loader-breathe h-20 w-20 select-none dark:hidden"
      />
      <img
        src="/logos/logo-mark-on-dark.svg"
        alt=""
        draggable={false}
        className="animate-loader-breathe hidden h-20 w-20 select-none dark:block"
      />
      <div className="relative mt-6 h-0.5 w-44 overflow-hidden bg-black/10 dark:bg-white/10">
        <div className="animate-loader-shimmer absolute inset-y-0 w-1/3 bg-[var(--color-brand-blue)]" />
      </div>
      <span className="sr-only">{t('loading', { defaultValue: 'Loading…' })}</span>
    </div>
  );
};

export default function RootLayout() {
  const { i18n: i18nInstance } = useTranslation();
  const location = useLocation();

  // SSG renders routes CONCURRENTLY against ONE i18n singleton. Prefixed
  // routes are protected by LangWrapper's per-language clones, but the
  // unprefixed EN pages read the singleton — whichever language a sibling
  // render set last won, which is how the production /pricing page shipped
  // with 100% Polish copy under lang="en". During SSR, bind the provider to
  // the URL's language clone instead (skip app/protected paths — their
  // language comes from the user preference mutating the singleton). On the
  // client nothing changes: one render at a time, no race, and the saved-
  // language restore keeps working through the singleton.
  const routedI18n =
    import.meta.env.SSR && !isLangSkippablePath(location.pathname)
      ? getI18nForLang(urlLangOf(location.pathname))
      : i18n;

  // Restore saved language after hydration to avoid mismatch
  useEffect(() => {
    restoreSavedLanguage();
  }, []);

  // Reveal the page once the SPA has mounted + routed. On a branded host
  // index.html hides <body> (.branded-boot) to avoid flashing the pre-rendered
  // marketing landing before the guard redirects to /auth or /dashboard.
  useEffect(() => {
    document.documentElement.classList.remove('branded-boot');
  }, []);

  // Keep <html dir> in sync with current language (lang is set by HreflangTags via Helmet)
  useEffect(() => {
    document.documentElement.dir = i18nInstance.language === 'ar' ? 'rtl' : 'ltr';
  }, [i18nInstance.language]);

  return (
    <ThemeProvider attribute="class" defaultTheme={typeof window !== 'undefined' && isBrandedHost() ? 'light' : 'dark'} storageKey="cctvplanner_theme" enableSystem={false}>
      <I18nextProvider i18n={routedI18n}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <UserPreferencesProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <HreflangTags />
                <SchemaScript schema={generateOrganizationSchema()} />
                <SchemaScript schema={generateWebSiteSchema()} />
                <RouteTracker />
                <HostBranding />
                <LanguageSuggestionBanner />
                <Suspense fallback={<RouteLoadingFallback />}>
                  <BrandedHostGuard>
                    <Outlet />
                  </BrandedHostGuard>
                </Suspense>
                <CookieConsent />
                {/* Per-account device limit (anti credential-sharing). Inert
                    while enforce=false (the shipped default) — renders null. */}
                {typeof window !== 'undefined' && <DeviceLimitGate />}
              </TooltipProvider>
            </UserPreferencesProvider>
          </AuthProvider>
        </QueryClientProvider>
      </I18nextProvider>
    </ThemeProvider>
  );
}
